import React, { useMemo, useEffect, useCallback } from 'react';
import { Button, Card, Col, Empty, Menu, notification, Row, Select, Tag, Typography } from 'antd';
import { useArticles } from 'hooks/useArticles';
import { ArticleCard } from './ArticleCard';
import { ArticleForm } from './ArticleForm';
import { ACLCheck, FieldSorter } from 'shared/Helpers';
import { Vendor } from 'types/vendor';
import uniqBy from 'lodash/uniqBy';
import { VendorFile, FileTag, Article } from 'types/Document';
import { CloseOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import { useSetRecoilState } from 'recoil';
import { selectedTopNavKeyState } from 'recoil/atoms';
import { useQuery } from 'hooks/useQuery';
import { MenuInfo } from 'rc-menu/lib/interface';

interface IProps {
  vendorId?: number;
  hideTabs?: boolean;
  hideActions?: boolean;
}

/**
 * Represents a list of articles.
 *
 * The ArticleList component is a functional component that displays a list of articles
 * based on various filters and categories. It takes in a set of props, including the
 * vendor ID, whether to hide tabs, and whether to hide actions. It uses local state,
 * Recoil state, and custom hooks to manage the data and interactions.
 * @component
 * @param {object} props - The props object.
 * @param {number} props.vendorId - The ID of the vendor.
 * @param {boolean} props.hideTabs - Whether to hide the tabs.
 * @param {boolean} props.hideActions - Whether to hide the actions.
 */
export const ArticleList: React.FC<IProps> = (props: IProps) => {
  const { vendorId, hideTabs, hideActions } = props;
  // LOCAL STATE
  const [activeTab, setActiveTab] = React.useState<string>('all');
  const { articlesData, getArticlesByCategory, allCategories, tags, useAddArticle } = useArticles();
  const { articles, articleCategories, isArticlesLoading } = articlesData;
  const [isAddArticleClicked, setIsAddArticleClicked] = React.useState(false);
  const [isAddArticleLoading, setIsAddArticleLoading] = React.useState(false);
  const [selectedVendor, setSelectedVendor] = React.useState<number | undefined>(undefined);
  const [selectedTag, setSelectedTag] = React.useState<number | undefined>(undefined);
  const { clearParams, getParam } = useQuery();
  const articleId = getParam('resource_id');
  const resourceType = getParam('resource_type');

  // RECOIL STATE
  const setSelectedTopNavKey = useSetRecoilState(selectedTopNavKeyState);
  useEffect(() => setSelectedTopNavKey(undefined), [setSelectedTopNavKey]);

  useEffect(() => {
    if (vendorId) {
      setSelectedVendor(vendorId);
    }
  }, [vendorId]);

  useEffect(() => {
    if (resourceType) {
      const category = articleCategories?.find(
        (category) => category.name.toLowerCase() === resourceType.toLowerCase()
      );
      setActiveTab(category?.id.toString() || 'all');
    }
  }, [resourceType]);

  const { mutate: addArticle } = useAddArticle({
    onSuccess: () => {
      notification.success({
        message: 'Article added successfully'
      });
      setIsAddArticleClicked(false);
      setIsAddArticleLoading(false);
    },
    onError: () => {
      notification.error({
        message: 'Error while adding article'
      });
      setIsAddArticleClicked(false);
      setIsAddArticleLoading(false);
    }
  });

  const menuItems = useMemo(() => {
    const tabs =
      articleCategories?.map((category) => ({
        key: category.id.toString(),
        label: category.name,
        children: category.child_categories?.map((child) => ({
          key: child.id.toString(),
          label: child.name
        }))
      })) || [];

    return [{ key: 'all', label: 'All Resources' }, ...tabs];
  }, [articleCategories]);

  /**
   * Memoized array of articles categorized based on the active tab.
   *
   * The categoryArticles variable is a memoized version of the array of articles
   * categorized based on the activeTab variable. It is created using the useMemo()
   * hook from React. The returned value of the useMemo() hook is a function that
   * returns an array of Article objects or undefined.
   *
   * If the activeTab is 'all', the function returns the entire articles array.
   * This means that all articles are considered to be in the 'all' category.
   * Otherwise, it calls the getArticlesByCategory() function passing the parsed
   * integer value of the activeTab as an argument. The getArticlesByCategory()
   * function is responsible for filtering and returning the articles that belong
   * to the specified category. The returned array will then be stored in the
   * categoryArticles variable.
   *
   * This memoized value is recalculated whenever any of the dependencies specified
   * in the dependency array [activeTab, articles, getArticlesByCategory] change.
   * If any of these dependencies change, the memoized value will be updated and the
   * component that uses this value will be re-rendered.
   * @returns {Article[] | undefined} - An array of Article objects or undefined
   * based on the active tab.
   */
  const categoryArticles = useMemo((): Article[] | undefined => {
    if (activeTab === 'all') return articles;
    return getArticlesByCategory(parseInt(activeTab));
  }, [activeTab, articles, getArticlesByCategory]);

  /**
   * The filteredArticles variable represents an array of articles that have been filtered based on certain criteria.
   * It is derived from the useMemo hook, which allows for memoization of the filtered articles.
   * The filtering is performed based on the values of categoryArticles, selectedVendor, selectedTag, and articleId.
   * If articleId is not provided, the filtering is performed based on selectedVendor and selectedTag.
   * If articleId is provided, the filtering is performed based on the matching articleId.
   * The filteredArticles variable can contain an array of Article objects, or it can be undefined, indicating that no articles match the filtering criteria.
   * The dependency array provided to the useMemo hook ensures that the filtering is recalculated when any of the dependencies change.
   */
  const filteredArticles = useMemo((): Article[] | undefined => {
    let filteredArticles = categoryArticles;
    if (!articleId) {
      if (selectedVendor) {
        filteredArticles = categoryArticles?.filter((item: Article) => {
          if (item.vendorFile.find((i: VendorFile) => i.vendor.id === selectedVendor)) {
            return item;
          }
        });
      }
      if (selectedTag) {
        filteredArticles = filteredArticles?.filter((item: Article) => {
          if (item.fileTag.find((i: FileTag) => i.tag.id === selectedTag)) {
            return item;
          }
        });
      }
    } else {
      filteredArticles = filteredArticles?.filter((item: Article) => item.id === Number(articleId));
    }
    return filteredArticles;
  }, [categoryArticles, selectedVendor, selectedTag, articleId]);

  /**
   * Renders a list of articles as a grid of ArticleCard components.
   * @returns {React.ReactElement} The rendered list of articles.
   */
  const renderArticles = (): React.ReactElement => (
    <Row gutter={[16, 24]}>
      {filteredArticles?.map((article: Article) => (
        <Col className="gutter-row" span={6} key={article.id}>
          <ArticleCard
            article={article}
            setSelectedVendor={setSelectedVendor}
            setSelectedTag={setSelectedTag}
            hideActions={hideActions}
          />
        </Col>
      ))}
    </Row>
  );

  /**
   * Returns an array of unique vendor tags filtered from category articles.
   * @returns {Vendor[]} An array of unique vendor tags filtered from category articles.
   * @throws {Error} When `categoryArticles` or `selectedVendor` is not provided.
   * @since 1.0.0
   */
  const getFilteredTags = useCallback((): Vendor[] => {
    const tags: Vendor[] = [];
    const filteredArticles = categoryArticles?.filter((item: Article) => {
      if (item.vendorFile.find((i: VendorFile) => i.vendor.id === selectedVendor)) {
        return item;
      }
    });
    filteredArticles?.map((i: Article) => {
      if (i.fileTag.length > 0) {
        i.fileTag.forEach((t: FileTag) => {
          tags.push(t.tag);
        });
        return i.fileTag;
      }
    });
    return uniqBy(tags.filter(Boolean), (obj) => obj.id);
  }, [categoryArticles, selectedVendor]);

  /**
   * Retrieves filtered suppliers based on selected tag and category articles.
   * @returns An array of vendors that meet the criteria.
   */
  const getFilteredSuppliers = useCallback((): Vendor[] => {
    let filteredArticles = categoryArticles;
    const suppliers: Vendor[] = [];
    if (selectedTag) {
      filteredArticles = filteredArticles?.filter((item: Article) => {
        if (item.fileTag.find((i: FileTag) => i.tag.id === selectedTag)) {
          return item;
        }
      });
    }
    filteredArticles?.map((i: Article) => {
      if (i.vendorFile.length > 0) {
        i.vendorFile.forEach((v: VendorFile) => {
          suppliers.push(v.vendor);
        });
        return i.vendorFile;
      }
    });
    return uniqBy(suppliers.filter(Boolean), (obj) => obj.id);
  }, [categoryArticles, selectedTag]);

  /**
   * Callback function called when the supplier changes.
   * @param {any} item - The selected item from the supplier dropdown.
   * @returns {void}
   */
  const onSupplierChangeHandler = (item: any): void => {
    setSelectedVendor(Number(item?.value) || undefined);
  };

  /**
   * A callback function that is triggered when the tag is changed.
   * @param {any} item - The input item parameter.
   * @returns {void}
   */
  const onTagChangeHandler = (item: any): void => {
    setSelectedTag(Number(item?.value) || undefined);
  };

  /**
   * Clears all filters.
   *
   * This function clears all the applied filters in the system.
   * It will reset the selected tag and vendor to undefined values
   * and clear any parameters set by the user.
   * @returns {void} This function does not return anything.
   */
  const clearAllFilters = (): void => {
    setSelectedTag(undefined);
    setSelectedVendor(undefined);
  };
  if (!isArticlesLoading && !articles?.length) {
    return <Empty description="No articles found" />;
  }

  // eslint-disable-next-line
  const filteredSuppliers = useMemo(() => {
    return getFilteredSuppliers();
  }, [getFilteredSuppliers]);

  // eslint-disable-next-line
  const filteredTags = useMemo(() => {
    if (selectedVendor) return getFilteredTags();
    return tags;
  }, [selectedVendor, getFilteredTags, tags]);

  /**
   * Clears the parameters and performs necessary actions when the selected article is closed.
   * @returns {void}
   */
  const handleSelectedArticleClose = (): void => {
    clearParams();
  };

  /**
   * Renders the filters component.
   * @returns {React.ReactElement} The rendered filters component.
   */
  const renderFilters = (): React.ReactElement => (
    <Row className="py-5 items-end">
      <Col flex="auto">
        <Select
          showSearch
          size={'large'}
          style={{ width: '300px' }}
          className="mr-4"
          filterOption={(input: string, option: undefined | any) => {
            if (option && option.children) {
              return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            } else {
              return false;
            }
          }}
          placeholder="Filter tags"
          value={selectedTag ? [tags?.find((i) => selectedTag === i.id)?.name] : selectedTag}
          allowClear
          onChange={onTagChangeHandler}
          labelInValue
        >
          {filteredTags?.map((v: any) => {
            return (
              <Select.Option key={v.id} id={v.id}>
                {v.name}
              </Select.Option>
            );
          })}
        </Select>
        <Select
          showSearch
          size={'large'}
          style={{ width: '300px' }}
          filterOption={(input: string, option: undefined | any) => {
            if (option && option.children) {
              return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            } else {
              return false;
            }
          }}
          placeholder="Filter supplier"
          value={selectedVendor ? [filteredSuppliers.find((i) => selectedVendor === i.id)?.name] : selectedVendor}
          allowClear
          onChange={onSupplierChangeHandler}
          labelInValue
        >
          {filteredSuppliers
            ?.sort((a, b) => FieldSorter(a.name, b.name))
            .map((v: any) => {
              return (
                <Select.Option key={v.id} id={v.id}>
                  {v.name}
                </Select.Option>
              );
            })}
        </Select>
        {(selectedTag || selectedVendor) && (
          <Button
            className="p-0 h-auto ml-4"
            key="clear-filters"
            type="link"
            icon={<UndoOutlined />}
            onClick={clearAllFilters}
          >
            Clear All
          </Button>
        )}
        {articleId && (
          <span style={{ paddingBottom: 8, display: 'inline-block' }}>
            <Tag
              className="rounded ml-4 px-3 py-2 text-sm"
              color={'#108ee9'}
              closeIcon={<CloseOutlined style={{ fontSize: 14, marginLeft: 4 }} />}
              closable={true}
              onClose={handleSelectedArticleClose}
            >
              {articles?.filter((article) => Number(articleId) === article.id)[0].name}
            </Tag>
          </span>
        )}
      </Col>
    </Row>
  );

  /**
   * Sets active tab and clears selected tag and vendor
   * @param {MenuInfo} e - The menu item information.
   * @returns {void}
   */
  const handleMenuClick = (e: MenuInfo): void => {
    setActiveTab(e.key);
    setSelectedTag(undefined);
    setSelectedVendor(undefined);
  };

  /**
   * Renders a list of articles.
   * @since VAN-613 Restored Add Resource button which was removed multiple weeks ago (MH)
   * @returns {React.ReactElement} A React element representing the list of articles.
   */
  const renderArticlesList = (): React.ReactElement => {
    return (
      <>
        {!hideTabs && <Menu onClick={handleMenuClick} selectedKeys={[activeTab]} mode="horizontal" items={menuItems} />}
        {!hideTabs && (
          <Row align="bottom" justify="space-between">
            <Col flex="auto">
              <Typography.Title level={5} className="!font-normal !pt-3 !mb-0 opacity-60">
                IT Sourcing Insights
              </Typography.Title>
              <Typography.Title level={4} className="!font-light !m-0 opacity-80">
                Stay on top of what&apos;s new, what&apos;s changing and what to look out for
              </Typography.Title>
            </Col>
            <Col>
              {ACLCheck(['create:npi_docs']) && (
                <div style={{ textAlign: 'right' }}>
                  <Button
                    type="default"
                    size="middle"
                    onClick={() => setIsAddArticleClicked(true)}
                    icon={<PlusOutlined />}
                  >
                    Add Resource
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        )}
        {(filteredArticles && filteredArticles.length > 0 && (
          <>
            {renderFilters()}
            {renderArticles()}
          </>
        )) || (
          <div className="mt-12">
            <Empty description="No resources available at this time ..." />
          </div>
        )}

        {isAddArticleClicked && ACLCheck(['create:npi_docs']) && (
          <ArticleForm
            tags={tags || []}
            allCategories={allCategories || []}
            isLoading={isAddArticleLoading}
            onCancel={() => {
              setIsAddArticleClicked(false);
              setIsAddArticleLoading(false);
            }}
            onSubmit={(payload: any) => {
              setIsAddArticleLoading(true);
              addArticle(payload);
            }}
          />
        )}
      </>
    );
  };

  return (
    <>
      {' '}
      {hideTabs ? (
        <div>{renderArticlesList()}</div>
      ) : (
        <Card style={{ width: '100%' }} className={isArticlesLoading ? 'pt-4' : ''} loading={isArticlesLoading}>
          {renderArticlesList()}
        </Card>
      )}
    </>
  );
};
